/**
 * EmbeddedSign — sandboxed SignNow iframe.
 *
 * Flow:
 *   1. Call `apiClient.signing.createEmbeddedInvites({...})` to mint the invite.
 *   2. Call `apiClient.signing.regenerateEmbeddedInviteLink({inviteId, email})`
 *      to pull a fresh signing URL.
 *   3. Render in a sandboxed iframe.
 *
 * Sandbox flags match the RCP portal: `allow-scripts allow-same-origin
 * allow-forms`. No `allow-top-navigation`.
 */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, Monitor } from 'lucide-react';
import { Button, Pill, WidgetCard } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import './rsc.css';

export function EmbeddedSign(): JSX.Element {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();

  const [email, setEmail] = useState('');
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const launch = async (): Promise<void> => {
    if (!sessionId || !email) return;
    setBusy(true);
    setError(null);
    try {
      // Minimal invite creation — wider contract comes in Wave 3 with session-aware UI.
      // NOTE (Wave 3): the create REQUEST shape is still a stub — CreateEmbeddedInvitesRequest
      // wants { DocumentGroupId, Invites:[{ Order, Signers:[...] }] }, which needs the cart's
      // SigningGroupId + signer roster threaded in. Tracked under BACKLOG-0748 follow-on.
      const create = await apiClient.signing.createEmbeddedInvites({
        SigningSessionId: sessionId,
      } as unknown as Parameters<typeof apiClient.signing.createEmbeddedInvites>[0]);
      // EmbeddedInvitesResponse is camelCase ({ invites:[{ embeddedInviteId, email,
      // signingLinkUrl }] }). The previous PascalCase cast ({ Invites:[{ InviteId, Email }] })
      // always read undefined, so no signer ever matched (BACKLOG-0748).
      const invites = create.invites ?? [];
      const match = invites.find((i) => (i.email ?? '').toLowerCase() === email.toLowerCase());
      const invite = match ?? invites[0];
      const inviteId = invite?.embeddedInviteId;
      if (!inviteId) {
        setError('No embedded invite found for this signer.');
        return;
      }
      // The create response already carries a usable signingLinkUrl; prefer it,
      // and fall back to regenerating a fresh link when absent.
      if (invite?.signingLinkUrl) {
        setIframeUrl(invite.signingLinkUrl);
        return;
      }
      const linkResp = await apiClient.signing.regenerateEmbeddedInviteLink({
        InviteId: inviteId,
        Email: email,
        AuthMethod: 'none',
        LinkExpirationMinutes: 30,
        SessionExpirationMinutes: 120,
      } as unknown as Parameters<typeof apiClient.signing.regenerateEmbeddedInviteLink>[0]);
      if (linkResp.success && linkResp.url) {
        setIframeUrl(linkResp.url);
      } else {
        setError(linkResp.error ?? 'Unable to regenerate signing link.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Launch failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rsc-drawer">
      <div className="rsc-detail__crumb">
        <Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>
          Back to session
        </Button>
        <h1>Embedded signer · {sessionId}</h1>
        <Pill variant="info">
          <Monitor size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />
          Sandboxed iframe
        </Pill>
      </div>

      {!iframeUrl && (
        <WidgetCard title="Launch signer">
          <div className="rsc-field">
            <label htmlFor="embed-email">Signer email</label>
            <input
              id="embed-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="rsc-footer">
            <Button variant="ghost" onClick={() => navigate('..')}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => void launch()} disabled={busy || !email}>
              {busy ? 'Launching…' : 'Launch signer'}
            </Button>
          </div>
        </WidgetCard>
      )}

      {error && (
        <div className="rsc-banner rsc-banner--err" role="alert">
          {error}
        </div>
      )}

      {iframeUrl && (
        <WidgetCard title="Signing surface">
          <iframe
            title={`SignNow embedded signer for ${email}`}
            src={iframeUrl}
            className="rsc-embed"
            sandbox="allow-scripts allow-same-origin allow-forms"
          />
          <div className="rsc-footer">
            <Button variant="ghost" onClick={() => setIframeUrl(null)}>
              Close
            </Button>
            <Button variant="primary" onClick={() => navigate('..')}>
              Done
            </Button>
          </div>
        </WidgetCard>
      )}
    </div>
  );
}
